import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import React from "react";

import { getOpsDetail } from "@/api/warehouse_ops";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { BottomSheet } from "@/lib/scan-ui/overlays";
import { LoadState } from "@/lib/scan-ui/screen";
import {
  ACCEPTANCE_OPS,
  detailColumns,
  detailValue,
  fmtDateTimeMsk,
  groupFactsByKind,
  KIND_TITLES,
  STORAGE_OPS,
} from "@/lib/warehouse-ops";

import { RefetchError } from "./RefetchError";

export interface OpsDrill {
  dateFrom: string;
  dateTo: string;
  direction: string;
  directionTitle: string;
  operation: string;
  operationTitle: string;
}

/** Провал до единиц за числом: паллеты сборки/отгрузки, отправления ФБС;
 *  хранение — позиции остатка и собранные паллеты за день (за период — по
 *  дням), приёмка — строки перемещений с упаковки. */
export function OpsDetailSheet({
  drill,
  onClose,
}: {
  drill: OpsDrill;
  onClose: () => void;
}): React.ReactElement {
  const q = useQuery({
    queryKey: ["warehouse-ops-detail", drill],
    queryFn: () => getOpsDetail(drill),
  });
  const fromMart = STORAGE_OPS.includes(drill.operation) || ACCEPTANCE_OPS.includes(drill.operation);
  const period =
    drill.dateFrom === drill.dateTo
      ? fmtDate(drill.dateFrom)
      : `${fmtDate(drill.dateFrom)} — ${fmtDate(drill.dateTo)}`;
  const undefinedDir = drill.direction === "undefined";
  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[85dvh] overflow-hidden flex flex-col">
      <div className="flex items-start justify-between gap-2 border-b p-3">
        <div>
          <div className="text-[15px] font-semibold">
            {drill.operationTitle} · {drill.directionTitle}
          </div>
          <div className="text-xs text-muted-foreground">
            {period}
            {q.data && !fromMart ? ` · ${q.data.facts.length} шт.` : ""}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Закрыть">
          <X className="size-4" />
        </Button>
      </div>
      <div className="overflow-auto p-3">
        {q.data && q.error && <RefetchError error={q.error} onRetry={() => void q.refetch()} />}
        {!q.data ? (
          <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
        ) : q.data.facts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ничего нет.</p>
        ) : (
          <div className="space-y-3">
            {groupFactsByKind(q.data.facts).map((g) => {
              const columns = detailColumns(drill.operation, g.kind || undefined);
              const showAt = !fromMart;
              return (
                <div key={g.kind || "facts"}>
                  {g.kind && (
                    <div
                      className={
                        g.kind === "shortfall"
                          ? "mb-1 text-xs font-semibold text-amber-800"
                          : "mb-1 text-xs font-semibold"
                      }
                    >
                      {KIND_TITLES[g.kind] ?? g.kind} · {g.facts.length}
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-muted-foreground">
                        <tr>
                          {showAt && <th className="py-1 pr-2 font-medium">Когда</th>}
                          {columns.map((c) => (
                            <th key={c.key} className="py-1 pr-2 font-medium">
                              {c.title}
                            </th>
                          ))}
                          {undefinedDir && !fromMart && (
                            <th className="py-1 pr-2 font-medium">Почему не определено</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {g.facts.map((f, i) => (
                          <tr key={i} className="border-t align-top">
                            {showAt && (
                              <td className="whitespace-nowrap py-1 pr-2">{fmtDateTimeMsk(f.at)}</td>
                            )}
                            {columns.map((c) => (
                              <td key={c.key} className="py-1 pr-2">
                                {detailValue(c.key, f.detail[c.key])}
                              </td>
                            ))}
                            {undefinedDir && !fromMart && (
                              <td className="py-1 pr-2 text-amber-800">{f.reason_title}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
